import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./App.css";
import { io } from 'socket.io-client';

const CATEGORIES = {
  WENT_WELL: "went_well",
  TO_IMPROVE: "to_improve",
  ACTION_ITEMS: "action_items",
};

const CATEGORY_TITLES = {
  went_well: "Went Well",
  to_improve: "To Improve",
  action_items: "Action Items",
};

const LobbyScreen = ({ onJoinRoom, onCreateRoom }) => {
  const [username, setUsername] = useState("");
  const [roomId, setRoomId] = useState("");
  const [activeTab, setActiveTab] = useState("join");

  const handleJoin = (e) => {
    e.preventDefault();
    if (!username.trim() || !roomId.trim()) return;
    onJoinRoom(username.trim(), roomId.trim());
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    onCreateRoom(username.trim());
  };

  return (
    <div className="lobby-container">
      <h1>Retrospective Board</h1>
      <div className="lobby-card">
        <div className="tab-buttons">
          <button
            className={`tab-button ${activeTab === "join" ? "active" : ""}`}
            onClick={() => setActiveTab("join")}
          >
            Join Room
          </button>
          <button
            className={`tab-button ${activeTab === "create" ? "active" : ""}`}
            onClick={() => setActiveTab("create")}
          >
            Create Room
          </button>
        </div>

        <div className="tab-content">
          {activeTab === "join" ? (
            <form onSubmit={handleJoin}>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your name..."
                required
              />
              <input
                type="text"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                placeholder="Room ID..."
                required
              />
              <button type="submit" className="submit-button">
                Join Room
              </button>
            </form>
          ) : (
            <form onSubmit={handleCreate}>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your name..."
                required
              />
              <button type="submit" className="submit-button">
                Create Room
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Socket.IO connection function
const createSocketConnection = ({ roomId, username, setConnected, handlers }) => {
  const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:10000';
  const qRoomId = String(roomId ?? '').trim();
  const qUsername = String(username ?? '').trim();

  const socket = io(SOCKET_URL, {
    query: { roomId: qRoomId, username: qUsername },
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    transports: ['websocket', 'polling'],
  });

  socket.on('initial-state', handlers.onInitialState);
  socket.on('item-added', handlers.onItemAdded);
  socket.on('item-removed', handlers.onItemRemoved);
  socket.on('item-moved', handlers.onItemMoved);
  socket.on('visibility-changed', handlers.onVisibilityChanged);

  socket.io.on("error", (error) => {
    console.error('Socket.IO Infrastructure Error:', error);
  });

  socket.io.on("reconnect_attempt", (attempt) => {
    console.log(`Reconnection attempt ${attempt}`);
  });

  socket.io.on("reconnect", (attempt) => {
    console.log(`Reconnected after ${attempt} attempts`);
  });

  socket.io.on("reconnect_error", (error) => {
    console.error('Reconnection error:', error);
  });

  socket.on('connect', () => {
    console.log('Socket.IO Connected');
    setConnected(true);
  });

  socket.on('disconnect', () => {
    console.log('Socket.IO Disconnected');
    setConnected(false);
  });

  socket.on('connect_error', (error) => {
    console.error('Socket.IO Connection Error:', error);
    setConnected(false);
  });

  return socket;
};

const Board = ({
  categoryId,
  items,
  removeItem,
  isRoomCreator,
  currentUser,
  isHidden,
}) => {
  return (
    <Droppable droppableId={categoryId}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`drop-zone ${
            snapshot.isDraggingOver ? "dragging-over" : ""
          }`}
        >
          {(items[categoryId] || []).map((item, index) => {
            const shouldHideContent =
              isHidden && item.author !== currentUser && !isRoomCreator;

            return (
              <Draggable key={item.id} draggableId={item.id} index={index}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`item ${snapshot.isDragging ? "dragging" : ""} ${
                      shouldHideContent ? "hidden-content" : ""
                    }`}
                    style={provided.draggableProps.style}
                  >
                    <div className="item-content">
                      {shouldHideContent ? (
                        <p>🔒 Hidden Content</p>
                      ) : (
                        <p>{item.content}</p>
                      )}
                      <div className="item-footer">
                        <small className="author">
                          Added by:{" "}
                          {shouldHideContent ? "—" : item.author}
                        </small>
                        {(item.author === currentUser || isRoomCreator) && (
                          <button
                            onClick={() => removeItem(categoryId, item.id)}
                            className="delete-button"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
            );
          })}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

const RetroBoard = ({ roomId, username, onDisconnect }) => {
  const [items, setItems] = useState({
    went_well: [],
    to_improve: [],
    action_items: [],
  });
  const [connected, setConnected] = useState(false);
  const [newItem, setNewItem] = useState("");
  const [activeCategory, setActiveCategory] = useState("went_well");
  const [socket, setSocket] = useState(null);
  const [isRoomCreator, setIsRoomCreator] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const emptyItems = {
      went_well: [],
      to_improve: [],
      action_items: [],
    };

    const handlers = {
      onInitialState: (data) => {
        setItems(data.items ?? emptyItems);
        setIsRoomCreator(
          Boolean(data?.isRoomCreator ?? data?.isRoomOwner)
        );
        setIsHidden(Boolean(data?.isHidden));
      },
      onItemAdded: ({ category, item }) => {
        setItems((prev) => ({
          ...prev,
          [category]: [...prev[category], item],
        }));
      },
      onItemRemoved: ({ category, itemId }) => {
        setItems((prev) => ({
          ...prev,
          [category]: prev[category].filter((item) => item.id !== itemId),
        }));
      },
      onItemMoved: ({ source, destination, item }) => {
        setItems((prev) => {
          const newItems = { ...prev };
          newItems[source.droppableId] = newItems[source.droppableId].filter(
            (i) => i.id !== item.id
          );
          newItems[destination.droppableId].splice(destination.index, 0, item);
          return newItems;
        });
      },
      onVisibilityChanged: ({ isHidden }) => {
        setIsHidden(isHidden);
      },
    };

    const socket = createSocketConnection({
      roomId,
      username,
      setConnected,
      handlers,
    });
    setSocket(socket);

    window.retroSocket = socket;

    return () => {
      socket.disconnect();
      window.retroSocket = null;
    };
  }, [roomId, username]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const addItem = (e) => {
    e.preventDefault();
    if (!newItem.trim() || !connected) return;

    const item = {
      id: `item-${Date.now()}`,
      content: newItem,
      author: username,
      likes: [],
    };

    socket.emit('add-item', {
      category: activeCategory,
      item,
    });

    setNewItem("");
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !connected) return;

    const { source, destination, draggableId } = result;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    socket.emit('move-item', {
      source,
      destination,
      itemId: draggableId,
    });
  };

  const removeItem = (category, itemId) => {
    if (!connected) return;

    socket.emit('remove-item', {
      category,
      itemId,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      addItem(e);
    }
  };

  const toggleVisibility = () => {
    if (!isRoomCreator) return;

    socket.emit('toggle-visibility');
  };

  return (
    <div className="App">
      <div className="room-info">
        <div className="room-header">
          <h1>Retrospective Board {connected ? "🟢" : "🔴"}</h1>
          <div className="room-details">
            <div className="room-meta">
              <p>
                <strong>Room ID:</strong> {roomId}
              </p>
              <p>
                <strong>User:</strong> {username}
              </p>
              {isRoomCreator && (
                <button
                  onClick={toggleVisibility}
                  className={`visibility-toggle ${isHidden ? "active" : ""}`}
                >
                  {isHidden ? "🔒 Private Mode" : "🔓 Public Mode"}
                </button>
              )}
            </div>
            <button onClick={onDisconnect} className="disconnect-button">
              Leave Room
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={addItem} className="input-section">
        <div className="input-group">
          <input
            ref={inputRef}
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Add a new item..."
            className="new-item-input"
            required
          />
        </div>
        <select
          value={activeCategory}
          onChange={(e) => setActiveCategory(e.target.value)}
          className="category-select"
        >
          {Object.values(CATEGORIES).map((categoryId) => (
            <option key={categoryId} value={categoryId}>
              {CATEGORY_TITLES[categoryId]}
            </option>
          ))}
        </select>
        <button type="submit" className="add-button">
          Add Item
        </button>
      </form>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="boards-container">
          {Object.values(CATEGORIES).map((categoryId) => (
            <div key={categoryId} className="board">
              <h2>{CATEGORY_TITLES[categoryId]}</h2>
              <Board
                categoryId={categoryId}
                items={items}
                removeItem={removeItem}
                isRoomCreator={isRoomCreator}
                currentUser={username}
                isHidden={isHidden}
              />
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};

const App = () => {
  const [roomState, setRoomState] = useState({
    inRoom: false,
    roomId: null,
    username: null,
  });

  const handleJoinRoom = (username, roomId) => {
    setRoomState({
      inRoom: true,
      roomId,
      username,
    });
  };

  const handleCreateRoom = async (username) => {
    const roomId = Math.random().toString(36).substring(2, 8).toUpperCase();
    setRoomState({
      inRoom: true,
      roomId,
      username,
    });
  };

  const handleDisconnect = () => {
    setRoomState({
      inRoom: false,
      roomId: null,
      username: null,
    });
  };

  return (
    <div>
      {!roomState.inRoom ? (
        <LobbyScreen
          onJoinRoom={handleJoinRoom}
          onCreateRoom={handleCreateRoom}
        />
      ) : (
        <RetroBoard
          roomId={roomState.roomId}
          username={roomState.username}
          onDisconnect={handleDisconnect}
        />
      )}
    </div>
  );
};

export default App;
